import { useCallback, useState } from "react";
import { Box, H5, P, Divider } from "orcs-design-system";
import { isEmpty } from "lodash";
import { useHistory } from "react-router-dom";
import { useGoalsExploreContext } from "src/contexts/goalsExploreContext";
import { useGetObjectives } from "src/contexts/goalsExploreContext/hooks";
import TreeViewNext from "src/shared/TreeViewNext";
import type { CustomizeTreeHeaderFn } from "src/shared/TreeViewNext";
import useFetchRootTeams from "src/hooks/useFetchRootTeams";
import { ROOT_TYPES_FILTER_KEY } from "src/consts/objectives";
import URL, { url } from "src/consts/urlPaths";
import ObjectiveTreeHeader from "./components/ObjectiveTreeHeader";
import useDisplayTreeData, {
  type DisplayTreeObjectiveNode,
} from "./hooks/useDisplayTreeData";

const LOAD_MORE_NODE_ID = "__load-more-objectives__";

type ObjectiveNode = DisplayTreeObjectiveNode;

const GoalsExplore = () => {
  const history = useHistory();
  const {
    treeData,
    loading,
    error,
    loadBranch,
    handleRootDataLoaded,
    handleAppendRootData,
  } = useGoalsExploreContext();

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const { rootTeams } = useFetchRootTeams({
    skip: !!selectedNodeId,
  } as never);

  const {
    loading: rootLoading,
    error: rootError,
    hasMore,
    totalCount,
    totalFetchedCount,
    loadingMore,
    handleLoadMore,
  } = useGetObjectives({
    onCompleted: handleRootDataLoaded(rootTeams),
    handleAppendRootData,
    rootTypesFilterKey: ROOT_TYPES_FILTER_KEY.GOALS_EXPLORER_ROOT,
    skip: !!selectedNodeId || isEmpty(rootTeams),
  } as never);

  const { displayTreeData, onNodeSelected: onDisplayNodeSelected } =
    useDisplayTreeData({
      treeData: treeData as ObjectiveNode[],
      hasMore,
      loadingMore,
      handleLoadMore,
      totalFetchedCount,
      totalCount,
    });

  const getGoalLinkTo = useCallback((node: ObjectiveNode) => {
    if (node.isLoadMore || node.noActivation || node.id === LOAD_MORE_NODE_ID) {
      return null;
    }
    if (node["objective"]) {
      return url(URL.GOALS_EXPLORE, {
        id: node["objective"]["id"] as string,
      });
    }
    if (node.group) {
      return url(URL.GOALS_EXPLORE, {});
    }
    return null;
  }, []);

  const onNodeSelected = useCallback(
    (node: ObjectiveNode) => {
      if (
        node.isLoadMore ||
        node.noActivation ||
        node.id === LOAD_MORE_NODE_ID
      ) {
        onDisplayNodeSelected(node);
        return;
      }

      setSelectedNodeId(node.id);

      const goalHref = getGoalLinkTo(node);
      if (goalHref) {
        history.push(goalHref);
      }

      onDisplayNodeSelected(node);
    },
    [getGoalLinkTo, history, onDisplayNodeSelected]
  );

  const onChevronToggle = useCallback(
    (node: ObjectiveNode, isOpen: boolean) => {
      if (!isOpen || node.group || node.isLoadMore || node.noActivation) {
        return;
      }
      loadBranch({ nodeId: node.id });
    },
    [loadBranch]
  );

  const customizeTreeHeader = useCallback<CustomizeTreeHeaderFn<ObjectiveNode>>(
    ({ node }, { onActivate }) => (
      <ObjectiveTreeHeader
        node={node}
        onActivate={onActivate}
        getGoalLinkTo={getGoalLinkTo}
      />
    ),
    [getGoalLinkTo]
  );

  const isLoading = loading || rootLoading;
  const combinedError = error || rootError;

  if (isLoading && isEmpty(treeData)) {
    return (
      <Box p="r">
        <P>Loading objectives...</P>
      </Box>
    );
  }

  if (combinedError) {
    return (
      <Box p="r">
        <P color="danger">
          Error loading objectives:{" "}
          {(combinedError as { message?: string })?.message ??
            String(combinedError)}
        </P>
      </Box>
    );
  }

  if (isEmpty(treeData)) {
    return (
      <Box p="r">
        <P>No objectives found</P>
      </Box>
    );
  }

  return (
    <Box>
      <H5 fontWeight="bold">Goals</H5>
      <Divider my="s" light />
      <TreeViewNext<ObjectiveNode>
        data={displayTreeData}
        onNodeSelected={onNodeSelected}
        onChevronToggle={onChevronToggle}
        suppressOnNodeSelectedWhenCollapsingBranch
        customizeTreeHeader={customizeTreeHeader}
        activeNodeId={selectedNodeId ?? undefined}
        openByDefault={false}
      />
    </Box>
  );
};

export default GoalsExplore;
